import type * as Y from 'yjs';
import { addBoardObject, boardObjects, boardTypes, seedBoard, updateBoardObject, type BoardObject, type BoardPoint } from './model.ts';
import { BoardView, type BoardViewOptions } from './renderer.ts';

export interface BoardEditor {
  canvas: HTMLCanvasElement;
  view: BoardView;
  dispose(): void;
}

export interface BoardEditorOptions extends BoardViewOptions {
  nowEpochMs?: () => number;
  clockSynchronized?: () => boolean;
}

function contains(object: BoardObject, point: BoardPoint): boolean {
  if (object.kind === 'stroke') {
    const points = object.points ?? [];
    if (points.length === 0) return false;
    const minX = Math.min(...points.map((item) => item.x)) - 14;
    const maxX = Math.max(...points.map((item) => item.x)) + 14;
    const minY = Math.min(...points.map((item) => item.y)) - 14;
    const maxY = Math.max(...points.map((item) => item.y)) + 14;
    return point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY;
  }
  return point.x >= object.x && point.x <= object.x + object.width && point.y >= object.y && point.y <= object.y + object.height;
}

export function mountBoardEditor(host: HTMLElement, doc: Y.Doc, viewOptions: BoardEditorOptions = {}): BoardEditor {
  host.innerHTML = `
    <section class="board-editor">
      <div class="board-toolbar">
        <div><p class="eyebrow">SHARED SURFACE</p><h2>Edit the real and rendered TV</h2></div>
        <div class="board-tools">
          <button type="button" data-add-note>+ Note</button>
          <button type="button" data-add-text>+ Text</button>
          <button type="button" data-add-rect>+ Rectangle</button>
          <button type="button" data-draw aria-pressed="false">Draw</button>
          <label>Colour <input type="color" data-board-color value="#f4c66d" /></label>
        </div>
      </div>
      <canvas data-board-canvas aria-label="Editable shared TV surface"></canvas>
      <label class="board-copy" data-board-copy hidden>Selected text<textarea rows="3" data-board-text></textarea></label>
      <p class="board-hint">Drag any item. Choose Draw, then drag on the board for a freehand stroke.</p>
    </section>`;
  const board = boardTypes(doc);
  seedBoard(doc, board);
  const canvas = host.querySelector<HTMLCanvasElement>('[data-board-canvas]')!;
  const color = host.querySelector<HTMLInputElement>('[data-board-color]')!;
  const drawButton = host.querySelector<HTMLButtonElement>('[data-draw]')!;
  const copyLabel = host.querySelector<HTMLElement>('[data-board-copy]')!;
  const textArea = host.querySelector<HTMLTextAreaElement>('[data-board-text]')!;
  const view = new BoardView(canvas, board, viewOptions);
  let selectedId: string | undefined;
  let drawMode = false;
  let dragging: { id: string; offsetX: number; offsetY: number } | undefined;
  let drawingId: string | undefined;
  const nowEpochMs = viewOptions.nowEpochMs ?? Date.now;
  const clockSynchronized = viewOptions.clockSynchronized ?? (() => false);

  const select = (object?: BoardObject): void => {
    selectedId = object?.id;
    const hasText = Boolean(object && (object.kind === 'note' || object.kind === 'text'));
    copyLabel.hidden = !hasText;
    textArea.value = hasText ? object!.text ?? '' : '';
  };

  const add = (kind: 'note' | 'text' | 'rect'): void => {
    const count = board.order.length;
    const offset = (count * 34) % 240;
    const created = addBoardObject(doc, board, kind === 'note'
      ? { kind, x: 130 + offset, y: 190 + offset * 0.45, width: 260, height: 190, color: color.value, text: 'A new shared note' }
      : kind === 'text'
        ? { kind, x: 160 + offset, y: 120 + offset * 0.4, width: 520, height: 76, color: color.value, text: 'Type on the shared screen' }
        : { kind, x: 210 + offset, y: 180 + offset * 0.35, width: 330, height: 210, color: color.value }, nowEpochMs(), clockSynchronized());
    select(created);
  };
  host.querySelector('[data-add-note]')?.addEventListener('click', () => add('note'));
  host.querySelector('[data-add-text]')?.addEventListener('click', () => add('text'));
  host.querySelector('[data-add-rect]')?.addEventListener('click', () => add('rect'));
  drawButton.addEventListener('click', () => {
    drawMode = !drawMode;
    drawButton.setAttribute('aria-pressed', String(drawMode));
    drawButton.textContent = drawMode ? 'Drawing…' : 'Draw';
  });
  color.addEventListener('input', () => {
    if (selectedId) updateBoardObject(doc, board, selectedId, { color: color.value }, nowEpochMs(), clockSynchronized());
  });
  textArea.addEventListener('input', () => {
    if (selectedId) updateBoardObject(doc, board, selectedId, { text: textArea.value }, nowEpochMs(), clockSynchronized());
  });

  canvas.addEventListener('pointerdown', (event) => {
    const point = view.toBoardPoint(event.clientX, event.clientY);
    canvas.setPointerCapture(event.pointerId);
    if (drawMode) {
      const stroke = addBoardObject(doc, board, { kind: 'stroke', x: 0, y: 0, width: 0, height: 0, color: color.value, points: [point] }, nowEpochMs(), clockSynchronized());
      drawingId = stroke.id;
      select(stroke);
      return;
    }
    const object = boardObjects(board).reverse().find((candidate) => contains(candidate, point));
    select(object);
    if (object) dragging = { id: object.id, offsetX: point.x - object.x, offsetY: point.y - object.y };
  });
  canvas.addEventListener('pointermove', (event) => {
    if (!canvas.hasPointerCapture(event.pointerId)) return;
    const point = view.toBoardPoint(event.clientX, event.clientY);
    if (drawingId) {
      const object = board.objects.get(drawingId);
      if (object) updateBoardObject(doc, board, drawingId, { points: [...(object.points ?? []), point] }, nowEpochMs(), clockSynchronized());
    } else if (dragging) {
      updateBoardObject(doc, board, dragging.id, { x: point.x - dragging.offsetX, y: point.y - dragging.offsetY }, nowEpochMs(), clockSynchronized());
    }
  });
  const endPointer = (event: PointerEvent): void => {
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
    drawingId = undefined;
    dragging = undefined;
  };
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);

  return { canvas, view, dispose: () => view.dispose() };
}
